// Capability model for the QR generator (ADR-023, Phase 1).
//
// Pure resolver — takes a `CapabilityContext` (caller is responsible for
// wiring oidc-spa + billing data into it) and returns a flat
// `GeneratorCapabilities` record that the UI gates its paywall stations
// against. Adding a new viewer profile (Trial, API-Key, …) is a new
// resolver branch, not a UI change.

import { FREEMIUM_PLUS_VANITY_LIMIT } from "@qrody/qr-renderer";

/**
 * Plan identifiers as returned by the backend `/v1/billing/plan` endpoint.
 *
 * Kept as a string-literal union so unknown plan ids fall through to the
 * conservative free-resolver branch — adding a new plan on the backend
 * cannot accidentally unlock paywalled features in the UI without a
 * matching frontend change.
 */
export type GeneratorPlanId = "tier1_free" | "pro_quarterly" | "pro_yearly";

/**
 * Inputs the resolver needs to make a verdict. Intentionally narrow:
 * the resolver knows nothing about OIDC, TanStack-Query, or how the
 * planId is fetched.
 */
export interface CapabilityContext {
  /** True iff oidc-spa reports a settled session. */
  isLoggedIn: boolean;
  /** Backend-reported plan; `null` when unknown (logged-out / loading). */
  planId: GeneratorPlanId | null;
}

/**
 * Flat capability record consumed by the UI. Stations must check
 * `caps.<flag>` instead of hardcoded constants or ad-hoc isLoggedIn
 * checks — this is the seam ADR-023 codifies.
 */
export interface GeneratorCapabilities {
  /** Hint copy + future dynamic-redirect controls (Phase 4+). */
  allowDynamic: boolean;
  /** Watermark lock-box — false renders the Pro-locked button. */
  allowWatermarkRemoval: boolean;
  /** Datei-Auflösungs-Cap. 300 = anonymous/free, 600 = Pro. */
  maxDpi: 300 | 600;
  /** Whether the logo-upload area is unlocked. */
  allowLogo: boolean;
  /**
   * Stack-Limit für vanity features (frame, ECC, custom colours).
   * `null` = unlimited (Pro).
   */
  vanityStackLimit: number | null;
  /** Primary action of the generator. Phase 2 wires the save path. */
  primaryAction: "download" | "save";
  /** Renders the marketing-style "Sign up" CTAs and dynamic-hint. */
  showAccountCTAs: boolean;
  /**
   * Pro-only knob: explicit QR-version floor (1–40) to enforce
   * consistent module counts across a print campaign or larger module
   * sizes for long-distance scans. Free / anon get the locked field +
   * upgrade prompt.
   */
  allowMinVersion: boolean;
}

const PRO_PLAN_IDS: ReadonlySet<GeneratorPlanId> = new Set([
  "pro_quarterly",
  "pro_yearly",
]);

/**
 * Caps for an unauthenticated visitor — mirrors the existing anonymous
 * generator's behaviour exactly so callers can use this as the safe
 * default during the OIDC bootstrap window.
 */
export const DEFAULT_ANONYMOUS_CAPABILITIES: GeneratorCapabilities = {
  allowDynamic: false,
  allowWatermarkRemoval: false,
  maxDpi: 300,
  allowLogo: false,
  vanityStackLimit: FREEMIUM_PLUS_VANITY_LIMIT,
  primaryAction: "download",
  showAccountCTAs: true,
  allowMinVersion: false,
};

/**
 * Three resolver paths today (anon / free / pro). Unknown plan ids on a
 * logged-in account fall through to the free-tier branch — defensive
 * against future backend additions.
 */
export function resolveCapabilities(
  ctx: CapabilityContext,
): GeneratorCapabilities {
  if (!ctx.isLoggedIn) {
    return DEFAULT_ANONYMOUS_CAPABILITIES;
  }

  if (ctx.planId !== null && PRO_PLAN_IDS.has(ctx.planId)) {
    return {
      allowDynamic: true,
      allowWatermarkRemoval: true,
      maxDpi: 600,
      allowLogo: true,
      vanityStackLimit: null,
      primaryAction: "save",
      showAccountCTAs: false,
      allowMinVersion: true,
    };
  }

  return {
    ...DEFAULT_ANONYMOUS_CAPABILITIES,
    primaryAction: "save",
    showAccountCTAs: false,
  };
}
